import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faEdit, 
  faTrash, 
  faChevronLeft,
  faTimes,
  faChevronDown,
  faChevronUp,
  faBars,
  faArrowUp,
  faArrowDown,
  faCheck,
  faMusic,
  faGuitar,
  faEllipsisV,
  faShare,
  faDownload,
  faUpload,
  faFont
} from '@fortawesome/free-solid-svg-icons';
import { doc, updateDoc, collection, addDoc, getDocs, deleteDoc, query, orderBy, serverTimestamp, Timestamp } from 'firebase/firestore';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import TourContext from '../../context/TourContext';
import UserContext from '../../context/UserContext';
import LeftMenu from '../LeftMenu';
import RightMenu from '../RightMenu';
import styles from './Setlist.module.css';

const SetlistModal = ({ isOpen, onClose, song, onSave, isProcessing }) => {
  const [trackName, setTrackName] = useState('');
  const [bpm, setBpm] = useState('');
  const [key, setKey] = useState('');
  const [selectedInstrument, setSelectedInstrument] = useState('');
  const [customInstrument, setCustomInstrument] = useState('');
  const [lyricsChords, setLyricsChords] = useState('');
  const [addedInstruments, setAddedInstruments] = useState([]);
  const [selectedInstrumentTag, setSelectedInstrumentTag] = useState(null);

  useEffect(() => {
    if (song) {
      setTrackName(song.trackName || '');
      setBpm(song.bpm || '');
      setKey(song.key || '');
      setAddedInstruments(song.instruments || []);
    } else {
      setTrackName('');
      setBpm('');
      setKey('');
      setAddedInstruments([]);
      setSelectedInstrument('');
      setCustomInstrument('');
      setLyricsChords('');
      setSelectedInstrumentTag(null);
    }
  }, [song]);

  const instruments = ['Guitar', 'Bass Guitar', 'Keyboard', 'Vocals', 'Drums', 'Custom'];

  const handleInstrumentSelect = (value) => {
    setSelectedInstrument(value);
    const selectedInst = addedInstruments.find(inst => inst.name === value);
    if (selectedInst) {
      setLyricsChords(selectedInst.lyricsChords);
      setSelectedInstrumentTag(value);
    } else {
      setLyricsChords('');
      setSelectedInstrumentTag(null);
    }
  };

  const handleAddInstrument = () => {
    if (selectedInstrument) {
      const instrumentName = selectedInstrument === 'Custom' ? customInstrument : selectedInstrument;
      if (!instrumentName) return;
      
      const updatedInstruments = addedInstruments.filter(inst => inst.name !== instrumentName);
      updatedInstruments.push({
        name: instrumentName,
        lyricsChords: lyricsChords
      });
      setAddedInstruments(updatedInstruments);
      setSelectedInstrument('');
      setCustomInstrument('');
      setLyricsChords('');
      setSelectedInstrumentTag(null);
    }
  };

  const handleInstrumentTagSelect = (instrumentName) => {
    setSelectedInstrumentTag(instrumentName);
    const selectedInst = addedInstruments.find(inst => inst.name === instrumentName);
    if (selectedInst) {
      const isCustom = !instruments.includes(instrumentName) || instrumentName === 'Custom';
      setSelectedInstrument(isCustom ? 'Custom' : instrumentName);
      setCustomInstrument(isCustom ? instrumentName : '');
      setLyricsChords(selectedInst.lyricsChords);
    }
  };

  const handleRemoveInstrument = (instrumentName) => {
    if (window.confirm(`Remove lyrics and chords for ${instrumentName}?`)) {
      const updatedInstruments = addedInstruments.filter(inst => inst.name !== instrumentName);
      setAddedInstruments(updatedInstruments);
      
      if (selectedInstrumentTag === instrumentName) {
        setSelectedInstrumentTag(null);
        setSelectedInstrument('');
        setLyricsChords('');
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Check if there are unsaved lyrics/chords
    if (selectedInstrument && lyricsChords.trim() !== '') {
      if (!window.confirm('You have unsaved lyrics and chords. To save them, you need to press "Add Lyrics and Chords" button first. Continue without saving them?')) {
        return;
      }
    }
    
    onSave({
      trackName,
      bpm,
      key,
      instruments: addedInstruments
    });
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2>{song ? 'Edit Song' : 'Add Song'}</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className={styles.modalForm}>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="trackName">Track Name *</label>
              <input
                id="trackName"
                type="text"
                value={trackName}
                onChange={(e) => setTrackName(e.target.value)}
                required
                className={styles.input}
                placeholder="Enter track name"
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="bpm">BPM</label>
              <input
                id="bpm"
                type="number"
                value={bpm}
                onChange={(e) => setBpm(e.target.value)}
                className={styles.input}
                placeholder="120"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="key">Key</label>
              <input
                id="key"
                type="text"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                className={styles.input}
                placeholder="C Major"
              />
            </div>
          </div>

          <div className={styles.lyricsSection}>
            <h3>Add Lyrics and Chords Chart</h3>
            
            {addedInstruments.length > 0 && (
              <div className={styles.instrumentTags}>
                <span className={styles.addedLabel}>Added for:</span>
                {addedInstruments.map((inst, index) => (
                  <div key={index} className={styles.tagContainer}>
                    <button
                      type="button"
                      className={`${styles.instrumentTag} ${selectedInstrumentTag === inst.name ? styles.selectedTag : ''}`}
                      onClick={() => handleInstrumentTagSelect(inst.name)}
                    >
                      {inst.name}
                    </button>
                    <button
                      type="button"
                      className={styles.removeTagButton}
                      onClick={() => handleRemoveInstrument(inst.name)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className={styles.formGroup}>
              <label htmlFor="instrument">Select Instrument</label>
              <select
                id="instrument"
                value={selectedInstrument}
                onChange={(e) => handleInstrumentSelect(e.target.value)}
                className={styles.select}
              >
                <option value="">Select Instrument</option>
                {instruments.map((inst) => (
                  <option key={inst} value={inst}>
                    {inst}
                  </option>
                ))}
              </select>
            </div>

            {selectedInstrument === 'Custom' && (
              <div className={styles.formGroup}>
                <label htmlFor="customInstrument">Custom Instrument Name</label>
                <input
                  id="customInstrument"
                  type="text"
                  value={customInstrument}
                  onChange={(e) => setCustomInstrument(e.target.value)}
                  className={styles.input}
                  placeholder="Enter custom instrument"
                />
              </div>
            )}

            {selectedInstrument && (
              <>
                <div className={styles.formGroup}>
                  <label htmlFor="lyricsChords">
                    Lyrics and Chords for {selectedInstrument === 'Custom' ? customInstrument || 'Custom' : selectedInstrument}
                  </label>
                  <textarea
                    id="lyricsChords"
                    value={lyricsChords}
                    onChange={(e) => setLyricsChords(e.target.value)}
                    className={styles.textarea}
                    rows={8}
                    placeholder="Enter lyrics and chords..."
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAddInstrument}
                  className={styles.addInstrumentButton}
                >
                  {selectedInstrumentTag ? 'Update' : 'Add'} Lyrics and Chords
                </button>
              </>
            )}
          </div>

          <div className={styles.modalActions}>
            <button
              type="button"
              onClick={onClose}
              className={styles.cancelButton}
              disabled={isProcessing}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : 'Save Song'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TextModal = ({ isOpen, onClose, onSave, isProcessing }) => {
  const [text, setText] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(text);
    setText('');
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.textModalContent}>
        <div className={styles.modalHeader}>
          <h2>Add Text</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className={styles.modalForm}>
          <p className={styles.textModalDescription}>
            You can add text to display more info in between your setlist, example: Intro, Break, Talk etc.
          </p>
          <div className={styles.formGroup}>
            <input
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              required
              className={styles.input}
              placeholder="Enter text"
            />
          </div>
          <div className={styles.modalActions}>
            <button
              type="button"
              onClick={onClose}
              className={styles.cancelButton}
              disabled={isProcessing}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Simple PDF Modal Component
const PdfModal = ({ isOpen, onClose, pdfHtml }) => {
  if (!isOpen) return null;

  const handlePrint = () => {
    // Create a hidden iframe for printing
    const printFrame = document.createElement('iframe');
    printFrame.style.display = 'none';
    document.body.appendChild(printFrame);
    
    printFrame.contentDocument.write(pdfHtml);
    printFrame.contentDocument.close();
    
    // Use setTimeout to avoid blocking
    setTimeout(() => {
      printFrame.contentWindow.print();
      // Remove iframe after a delay
      setTimeout(() => {
        document.body.removeChild(printFrame);
      }, 100);
    }, 100);
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.pdfModalContent}>
        <div className={styles.pdfModalHeader}>
          <h2>Setlist Preview</h2>
          <div className={styles.pdfModalActions}>
            <button className={styles.printButton} onClick={handlePrint}>
              <FontAwesomeIcon icon={faShare} />
              Print
            </button>
            <button className={styles.closeButton} onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>
        <div className={styles.pdfModalBody}>
          <iframe
            srcDoc={pdfHtml}
            className={styles.pdfIframe}
            title="Setlist Preview"
          />
        </div>
      </div>
    </div>
  );
};

const Setlist = () => {
  const navigate = useNavigate();
  const { eventId, dateId } = useParams();
  const location = useLocation();
  const { selectedEvent, getSelectedDate, updateDate } = useContext(TourContext);
  const { userData } = useContext(UserContext);
  
  const [setlist, setSetlist] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTextModalOpen, setIsTextModalOpen] = useState(false);
  const [editingSong, setEditingSong] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [expandedSong, setExpandedSong] = useState(null);
  const [expandedInstrument, setExpandedInstrument] = useState(null);
  const [reorderMode, setReorderMode] = useState(false);
  const [selectedSongIndex, setSelectedSongIndex] = useState(null);
  const [tempSetlist, setTempSetlist] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfHtml, setPdfHtml] = useState('');

  // Determine event type from URL or selected event
  const eventType = selectedEvent?.type || (location.pathname.includes('/tour/') ? 'tour' : 'show');

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadSetlist();
  }, [eventId, dateId, selectedEvent]);

  const loadSetlist = async () => {
    if (!eventId || !dateId) return;
    
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Query the setlist subcollection
      const setlistQuery = query(
        collection(db, collectionName, eventId, 'eventDates', dateId, 'setlist'),
        orderBy('order', 'asc')
      );
      
      const snapshot = await getDocs(setlistQuery);
      const setlistItems = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setSetlist(setlistItems);
    } catch (error) {
      console.error('Error loading setlist:', error);
    }
  };

  const handleAddSong = () => {
    setEditingSong(null);
    setIsModalOpen(true);
  };

  const handleEditSong = (song) => {
    setEditingSong(song);
    setIsModalOpen(true);
  };

  const handleBack = () => {
    navigate(`/${eventType}/${selectedEvent.id}/${dateId}`);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleSaveSong = async (songData) => {
    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Get the current setlist to determine the order
      let order = setlist.length + 1;
      
      if (editingSong) {
        // Update existing song
        const songRef = doc(db, collectionName, eventId, 'eventDates', dateId, 'setlist', editingSong.id);
        await updateDoc(songRef, {
          ...songData,
          updatedAt: serverTimestamp()
        });
      } else {
        // Add new song
        await addDoc(
          collection(db, collectionName, eventId, 'eventDates', dateId, 'setlist'), 
          {
            ...songData,
            order,
            type: 'song',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          }
        );
      }

      // Update the event date to trigger listener
      await updateDoc(
        doc(db, collectionName, eventId, 'eventDates', dateId),
        { dateLastUpdated: serverTimestamp() }
      );

      // Refresh the setlist
      await loadSetlist();
      
      // Let the listener update the state
      await updateDate(eventId, dateId, eventType);
      setIsModalOpen(false);

    } catch (error) {
      console.error('Error saving song:', error);
      alert('Failed to save song. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveText = async (text) => {
    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Get the current setlist to determine the order
      let order = setlist.length + 1;
      
      // Add text entry
      await addDoc(
        collection(db, collectionName, eventId, 'eventDates', dateId, 'setlist'), 
        {
          text,
          order,
          type: 'text',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        }
      );

      // Update the event date to trigger listener
      await updateDoc(
        doc(db, collectionName, eventId, 'eventDates', dateId),
        { dateLastUpdated: serverTimestamp() }
      );

      // Refresh the setlist
      await loadSetlist();
      
      // Let the listener update the state
      await updateDate(eventId, dateId, eventType);
      setIsTextModalOpen(false);

    } catch (error) {
      console.error('Error saving text:', error);
      alert('Failed to save text. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteSong = async (songId) => {
    if (!window.confirm('Are you sure you want to delete this song?')) return;

    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Delete the song document
      await deleteDoc(
        doc(db, collectionName, eventId, 'eventDates', dateId, 'setlist', songId)
      );

      // Get remaining items to reorder
      const remainingItems = setlist.filter(item => item.id !== songId);
      
      // Update order for all remaining items
      for (let i = 0; i < remainingItems.length; i++) {
        const item = remainingItems[i];
        const itemRef = doc(db, collectionName, eventId, 'eventDates', dateId, 'setlist', item.id);
        await updateDoc(itemRef, {
          order: i + 1,
          updatedAt: serverTimestamp()
        });
      }

      // Update the event date to trigger listener
      await updateDoc(
        doc(db, collectionName, eventId, 'eventDates', dateId),
        { dateLastUpdated: serverTimestamp() }
      );

      // Refresh the setlist
      await loadSetlist();
      
      // Let the listener update the state
      await updateDate(eventId, dateId, eventType);

    } catch (error) {
      console.error('Error deleting song:', error);
      alert('Failed to delete song. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteText = async (textId) => {
    if (!window.confirm('Are you sure you want to delete this text?')) return;

    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Delete the text document
      await deleteDoc(
        doc(db, collectionName, eventId, 'eventDates', dateId, 'setlist', textId)
      );

      // Get remaining items to reorder
      const remainingItems = setlist.filter(item => item.id !== textId);
      
      // Update order for all remaining items
      for (let i = 0; i < remainingItems.length; i++) {
        const item = remainingItems[i];
        const itemRef = doc(db, collectionName, eventId, 'eventDates', dateId, 'setlist', item.id);
        await updateDoc(itemRef, {
          order: i + 1,
          updatedAt: serverTimestamp()
        });
      }

      // Update the event date to trigger listener
      await updateDoc(
        doc(db, collectionName, eventId, 'eventDates', dateId),
        { dateLastUpdated: serverTimestamp() }
      );

      // Refresh the setlist
      await loadSetlist();
      
      // Let the listener update the state
      await updateDate(eventId, dateId, eventType);

    } catch (error) {
      console.error('Error deleting text:', error);
      alert('Failed to delete text. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExportJSON = () => {
    const eventDate = getSelectedDate();
    const formattedDate = eventDate?.date.split('-').reverse().join('-');
    const fileName = `setlist-${formattedDate}.json`;

    const data = setlist.map((item) => ({
      id: item.id,
      trackName: item.trackName || '',
      bpm: item.bpm || '',
      key: item.key || '',
      text: item.text || '',
      type: item.type || 'song',
      order: item.order || 0,
      instruments: item.instruments ? item.instruments.map(instrument => ({
        name: instrument.name,
        lyricsChords: instrument.lyricsChords
      })) : []
    }));

    const jsonData = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setIsMenuOpen(false);
  };

  const handleImportJSON = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsProcessing(true);
    try {
      const reader = new FileReader();
      
      const fileContents = await new Promise((resolve, reject) => {
        reader.onload = e => resolve(e.target.result);
        reader.onerror = e => reject(e);
        reader.readAsText(file);
      });
      
      const importedData = JSON.parse(fileContents);
      
      // Clear existing setlist
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Delete all existing setlist items
      const existingSetlistQuery = query(
        collection(db, collectionName, eventId, 'eventDates', dateId, 'setlist')
      );
      
      const existingSetlistSnapshot = await getDocs(existingSetlistQuery);
      
      for (const doc of existingSetlistSnapshot.docs) {
        await deleteDoc(doc.ref);
      }
      
      // Add imported items with proper order
      for (let i = 0; i < importedData.length; i++) {
        const item = importedData[i];
        
        await addDoc(
          collection(db, collectionName, eventId, 'eventDates', dateId, 'setlist'),
          {
            ...item,
            order: i + 1,
            type: item.text ? 'text' : 'song',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          }
        );
      }

      // Update the event date to trigger listener
      await updateDoc(
        doc(db, collectionName, eventId, 'eventDates', dateId),
        { dateLastUpdated: serverTimestamp() }
      );

      // Refresh the setlist
      await loadSetlist();
      
      // Let the listener update the state
      await updateDate(eventId, dateId, eventType);
      
      alert('Setlist imported successfully');
    } catch (error) {
      console.error('Error importing setlist:', error);
      alert('Failed to import setlist. Please check the file format.');
    } finally {
      setIsProcessing(false);
      setIsMenuOpen(false);
    }
  };

  const handleExportPDF = async () => {
    setIsMenuOpen(false);
    try {
      const html = generateSetlistHtml();
      setPdfHtml(html);
      setShowPdfModal(true);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF. Please try again.');
    }
  };

  const generateSetlistHtml = () => {
    const eventDate = getSelectedDate();
    const formattedDate = eventDate?.date.split('-').reverse().join('-');
    let songCount = 0;

    const rows = setlist
      .map((item) => {
        if (item.type === 'song' && item.trackName) {
          return `
            <tr>
              <td>${++songCount}</td>
              <td>${item.trackName}</td>
              <td>${item.bpm || ''}</td>
              <td>${item.key || ''}</td>
            </tr>
          `;
        } else if (item.type === 'text' && item.text) {
          return `
            <tr>
              <td style="border: none;"></td>
              <td colspan="3" style="font-style: italic; font-weight: bold;">${item.text}</td>
            </tr>
          `;
        }
        return '';
      })
      .filter(Boolean);

    return `
      <html>
        <head>
          <style>
            @page {
              size: A4;
              margin: 20mm;
            }
            body {
              font-family: Arial, sans-serif;
              font-size: 12px;
            }
            h1, h2, h3 {
              text-align: center;
              margin: 10px 0;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th, td {
              border: 1px solid black;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #f2f2f2;
              font-weight: bold;
              text-align: center;
            }
            td {
              text-align: center;
            }
          </style>
        </head>
        <body>
          <h1>${selectedEvent.tourName || selectedEvent.showName}</h1>
          <h2>${selectedEvent.actName || ''}</h2>
          <h3>${formattedDate}</h3>
          <h2>Setlist</h2>
          <table>
            <thead>
              <tr>
                <th>Serial No.</th>
                <th>Track Name</th>
                <th>BPM</th>
                <th>KEY</th>
              </tr>
            </thead>
            <tbody>
              ${rows.join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;
  };

  const [longPressTimer, setLongPressTimer] = useState(null);

  const activateReorderMode = (index) => {
    setReorderMode(true);
    setSelectedSongIndex(index);
    setTempSetlist([...setlist]);
  };

  const handleMouseDown = (index) => {
    const timer = setTimeout(() => {
      activateReorderMode(index);
    }, 500); // 500ms for long press
    setLongPressTimer(timer);
  };

  const handleMouseUp = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  const handleMouseLeave = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  const moveUp = () => {
    if (selectedSongIndex > 0) {
      const newSetlist = [...tempSetlist];
      [newSetlist[selectedSongIndex - 1], newSetlist[selectedSongIndex]] = 
      [newSetlist[selectedSongIndex], newSetlist[selectedSongIndex - 1]];
      setTempSetlist(newSetlist);
      setSelectedSongIndex(selectedSongIndex - 1);
    }
  };

  const moveDown = () => {
    if (selectedSongIndex < tempSetlist.length - 1) {
      const newSetlist = [...tempSetlist];
      [newSetlist[selectedSongIndex], newSetlist[selectedSongIndex + 1]] = 
      [newSetlist[selectedSongIndex + 1], newSetlist[selectedSongIndex]];
      setTempSetlist(newSetlist);
      setSelectedSongIndex(selectedSongIndex + 1);
    }
  };

  const saveReorder = async () => {
    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Update order for all items
      for (let i = 0; i < tempSetlist.length; i++) {
        const item = tempSetlist[i];
        const itemRef = doc(db, collectionName, eventId, 'eventDates', dateId, 'setlist', item.id);
        await updateDoc(itemRef, {
          order: i + 1,
          updatedAt: serverTimestamp()
        });
      }

      // Update the event date to trigger listener
      await updateDoc(
        doc(db, collectionName, eventId, 'eventDates', dateId),
        { dateLastUpdated: serverTimestamp() }
      );

      // Refresh the setlist
      await loadSetlist();
      
      setReorderMode(false);
      setSelectedSongIndex(null);

    } catch (error) {
      console.error('Error saving new order:', error);
      alert('Failed to save the new order. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const cancelReorder = () => {
    setReorderMode(false);
    setSelectedSongIndex(null);
  };

  // Function to calculate serial number for songs (excluding text rows)
  const getSerialNumber = (index) => {
    let count = 0;
    const list = reorderMode ? tempSetlist : setlist;
    for (let i = 0; i <= index && i < list.length; i++) {
      const item = list[i];
      if (item && item.type === 'song') {
        count++;
      }
    }
    return count;
  };

  if (!selectedEvent || !dateId) {
    return (
      <div className={styles.layout}>
        <main className={styles.main}>
          <div className={styles.emptyState}>
            <FontAwesomeIcon icon={faMusic} size="2x" />
            <p>Please select an event and date to view setlist</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={styles.layout}>
      <div className={styles.mainLayout}>
        {!isMobile && <LeftMenu selectedEvent={selectedEvent} />}
        <main className={styles.content}>
          <div className={styles.header}>
            <button className={styles.backButton} onClick={handleBack}>
              <FontAwesomeIcon icon={faChevronLeft} />
              Back to Schedule
            </button>
            <h1>{selectedEvent.tourName || selectedEvent.showName}</h1>
            <h2>{formatDate(getSelectedDate()?.date)}</h2>
          </div>

          <div className={styles.setlistSection}>
            <div className={styles.sectionHeader}>
              <h2>Setlist</h2>
              
              {/* 3-dot menu */}
              <div className={styles['menu-container']}>
                <button 
                  className={styles['menu-button']}
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                >
                  <FontAwesomeIcon icon={faEllipsisV} />
                </button>
                
                {isMenuOpen && (
                  <div className={styles['dropdown-menu']}>
                    <button 
                      className={styles['menu-item']}
                      onClick={handleExportPDF}
                    >
                      <FontAwesomeIcon icon={faShare} />
                      Share PDF
                    </button>
                    <button 
                      className={styles['menu-item']}
                      onClick={handleExportJSON}
                    >
                      <FontAwesomeIcon icon={faDownload} />
                      Export JSON
                    </button>
                    <label className={styles['menu-item']}>
                      <FontAwesomeIcon icon={faUpload} />
                      Import JSON
                      <input
                        type="file"
                        accept=".json"
                        onChange={handleImportJSON}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </div>
                )}
              </div>
            </div>

            {reorderMode && (
              <div className={styles.reorderControls}>
                <button onClick={moveUp} className={styles.reorderButton}>
                  <FontAwesomeIcon icon={faArrowUp} />
                </button>
                <button onClick={moveDown} className={styles.reorderButton}>
                  <FontAwesomeIcon icon={faArrowDown} />
                </button>
                <button onClick={saveReorder} className={styles.reorderSaveButton}>
                  <FontAwesomeIcon icon={faCheck} />
                  <span>Save Order</span>
                </button>
                <button onClick={cancelReorder} className={styles.reorderCancelButton}>
                  <FontAwesomeIcon icon={faTimes} />
                  <span>Cancel</span>
                </button>
              </div>
            )}

            <div className={styles.tableContainer}>
              {!reorderMode && setlist.length > 0 && (
                <p className={styles.reorderHint}>Long press on a row to reorder</p>
              )}
              <table className={styles.setlistTable}>
                <thead>
                  <tr>
                    <th>Serial No.</th>
                    <th>Track Name</th>
                    <th>BPM</th>
                    <th>KEY</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(reorderMode ? tempSetlist : setlist).map((item, index) => (
                    <React.Fragment key={item.id}>
                      <tr 
                        className={`${styles.tableRow} ${expandedSong === item.id ? styles.expandedRow : ''} ${reorderMode && selectedSongIndex === index ? styles.selectedRow : ''} ${item.type === 'text' ? styles.textRow : ''}`}
                        onClick={() => item.type === 'song' && setExpandedSong(expandedSong === item.id ? null : item.id)}
                        onMouseDown={() => handleMouseDown(index)}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseLeave}
                        onTouchStart={() => handleMouseDown(index)}
                        onTouchEnd={handleMouseUp}
                      >
                        {item.type === 'song' ? (
                          <>
                            <td>{getSerialNumber(index)}</td>
                            <td>{item.trackName}</td>
                            <td>{item.bpm || '-'}</td>
                            <td>{item.key || '-'}</td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <div className={styles.actionContainer}>
                                <button
                                  onClick={() => handleEditSong(item)}
                                  className={styles.actionButton}
                                >
                                  <FontAwesomeIcon icon={faEdit} />
                                </button>
                                <button
                                  onClick={() => handleDeleteSong(item.id)}
                                  className={styles.actionButton}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </div>
                            </td>
                          </>
                        ) : item.type === 'text' ? (
                          <>
                            <td></td>
                            <td colSpan="3" className={styles.textItem}>{item.text}</td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <div className={styles.actionContainer}>
                                <button
                                  onClick={() => handleDeleteText(item.id)}
                                  className={styles.actionButton}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </div>
                            </td>
                          </>
                        ) : null}
                      </tr>
                      {expandedSong === item.id && item.instruments && item.instruments.length > 0 && (
                        <tr>
                          <td colSpan="5" className={styles.instrumentsCell}>
                            <div className={styles.instrumentsContainer}>
                              {item.instruments.map((instrument, idx) => (
                                <div key={idx} className={styles.instrumentItem}>
                                  <div 
                                    className={styles.instrumentHeader}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setExpandedInstrument(expandedInstrument === `${item.id}-${idx}` ? null : `${item.id}-${idx}`);
                                    }}
                                  >
                                    <span className={styles.instrumentName}>
                                      <FontAwesomeIcon icon={faGuitar} />
                                      {instrument.name}
                                    </span>
                                    <FontAwesomeIcon 
                                      icon={expandedInstrument === `${item.id}-${idx}` ? faChevronUp : faChevronDown}
                                    />
                                  </div>
                                  {expandedInstrument === `${item.id}-${idx}` && (
                                    <pre className={styles.lyricsText}>{instrument.lyricsChords}</pre>
                                  )}
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  <tr className={styles.addRow}>
                    <td></td>
                    <td colSpan="3" className={styles.addRowText}>
                      <button onClick={handleAddSong} className={styles.inlineAddButton}>
                        <FontAwesomeIcon icon={faPlus} /> Add Song
                      </button>
                      or
                      <button onClick={() => setIsTextModalOpen(true)} className={styles.inlineAddButton}>
                        <FontAwesomeIcon icon={faFont} /> Add Text
                      </button>
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {isModalOpen && (
            <SetlistModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              song={editingSong}
              onSave={handleSaveSong}
              isProcessing={isProcessing}
            />
          )}

          {isTextModalOpen && (
            <TextModal
              isOpen={isTextModalOpen}
              onClose={() => setIsTextModalOpen(false)}
              onSave={handleSaveText}
              isProcessing={isProcessing}
            />
          )}

          {showPdfModal && (
            <PdfModal
              isOpen={showPdfModal}
              onClose={() => setShowPdfModal(false)}
              pdfHtml={pdfHtml}
            />
          )}

          {isProcessing && (
            <div className={styles.loadingOverlay}>
              <div className={styles.loadingSpinner}></div>
              <p className={styles.loadingText}>Processing...</p>
            </div>
          )}
        </main>
        {!isMobile && (
          <RightMenu
            selectedEvent={selectedEvent}
            events={[]}
            onEventSelect={() => {}}
          />
        )}
      </div>
    </div>
  );
};

export default Setlist;